import { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';

// Global reference to prevent duplicate processing across multiple component instances
let globalProcessingRef: { current: boolean } = { current: false };
let globalCallbackProcessedRef: { current: string | null } = { current: null };

interface OAuthCallbackParams {
  code?: string;
  state?: string;
  error?: string;
}

interface CallbackState {
  isProcessing: boolean;
  isSuccess: boolean;
  isError: boolean;
  errorMessage?: string;
  params: OAuthCallbackParams;
}

interface UseMercadoPagoOAuthCallbackProps {
  onSuccess?: () => void;
}

/**
 * Hook to handle MercadoPago OAuth callback
 */
export const useMercadoPagoOAuthCallback = (props?: UseMercadoPagoOAuthCallbackProps) => {
  const { onSuccess } = props || {};
  const location = useLocation();
  const navigate = useNavigate();
  const processingRef = useRef(false);
  const [callbackState, setCallbackState] = useState<CallbackState>({
    isProcessing: false,
    isSuccess: false,
    isError: false,
    params: {}
  });

  const processOAuthCallback = useCallback(async (params: OAuthCallbackParams) => {
    try {
      // Check if there's an error from MercadoPago
      if (params.error) {
        console.error('❌ Error en callback de MercadoPago:', params.error);
        setCallbackState(prev => ({
          ...prev,
          isProcessing: false,
          isError: true,
          errorMessage: `Error de MercadoPago: ${params.error}`
        }));
        return;
      }

      // Validate required parameters
      if (!params.code || !params.state) {
        console.error('❌ Parámetros de callback incompletos:', params);
        setCallbackState(prev => ({
          ...prev,
          isProcessing: false,
          isError: true,
          errorMessage: 'Parámetros de autorización incompletos'
        }));
        return;
      }

      // Validate state (CSRF protection)
      // Try localStorage first (new approach), then sessionStorage (backward compatibility)
      const storedState = localStorage.getItem('mercadopago_oauth_state') || sessionStorage.getItem('mercadopago_oauth_state');
      console.log('🔍 Validando estado OAuth:', {
        storedStateFromLocalStorage: localStorage.getItem('mercadopago_oauth_state'),
        storedStateFromSessionStorage: sessionStorage.getItem('mercadopago_oauth_state'),
        storedState,
        receivedState: params.state,
        localStorageKeys: Object.keys(localStorage),
        sessionStorageKeys: Object.keys(sessionStorage),
        isStateValid: storedState === params.state,
        isIncognitoMode: typeof Storage !== 'undefined' && localStorage.length === 0
      });
      
      if (!storedState) {
        console.warn('⚠️ No se encontró estado OAuth guardado en localStorage ni sessionStorage');
        console.log('📝 Esto puede ocurrir si:');
        console.log('   1. El usuario abrió MercadoPago en una nueva ventana/tab');
        console.log('   2. La sesión del navegador se reinició');
        console.log('   3. El localStorage/sessionStorage fue limpiado (ej: modo incógnito)');
        console.log('🔄 Procediendo sin validación de estado (modo recuperación)...');
        // En modo incógnito o cuando el estado se perdió, continuamos sin validación
      } else if (storedState !== params.state) {
        console.error('❌ Estado OAuth inválido. Stored:', storedState, 'Received:', params.state);
        console.warn('⚠️ Pero continuaremos procesando el callback de todas formas...');
        // No retornamos aquí - continuamos con el procesamiento
      }

      console.log('✅ Callback OAuth válido, enviando al backend...');
      
      // Send the OAuth callback to the backend
      const response = await apiClient.post(
        `/payment/oauth/mercadopago/complete/`,
        {
          code: params.code,
          state: params.state
        }
      );
      
      console.log('✅ Callback procesado exitosamente por el backend:', response.data);
      
      // Set success state
      setCallbackState(prev => ({
        ...prev,
        isProcessing: false,
        isSuccess: true,
        isError: false
      }));
      
      // Clean up storage
      localStorage.removeItem('mercadopago_oauth_state');
      sessionStorage.removeItem('mercadopago_oauth_state'); // Also clean sessionStorage for backward compatibility
      
      // Notify parent component of success
      if (onSuccess) {
        onSuccess();
      }
      
    } catch (error) {
      console.error('❌ Error procesando callback OAuth:', error);
      setCallbackState(prev => ({
        ...prev,
        isProcessing: false,
        isError: true,
        errorMessage: 'Error procesando la autorización'
      }));
    }
  }, [onSuccess]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const isMercadoPagoCallback = searchParams.get('mercadopago_callback') === 'true';
    
    if (!isMercadoPagoCallback) return;

    // Extract OAuth callback parameters for unique identification
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const callbackId = `${code}-${state}`;

    // Prevent duplicate processing globally across all component instances
    if (globalProcessingRef.current) {
      console.log('🔄 OAuth callback ya está siendo procesado globalmente, ignorando...');
      return;
    }

    // Check if this specific callback was already processed
    if (globalCallbackProcessedRef.current === callbackId) {
      console.log('🔄 Este callback específico ya fue procesado, ignorando...', { callbackId });
      return;
    }

    // Prevent duplicate processing in React Strict Mode (component level)
    if (processingRef.current) {
      console.log('🔄 OAuth callback ya está siendo procesado en este componente, ignorando...');
      return;
    }

    // Extract OAuth callback parameters
    const params: OAuthCallbackParams = {
      code: code || undefined,
      state: state || undefined,
      error: searchParams.get('error') || undefined,
    };

    console.log('🔄 MercadoPago OAuth callback detectado:', { params, callbackId });

    // Set all processing flags
    globalProcessingRef.current = true;
    processingRef.current = true;
    setCallbackState(prev => ({
      ...prev,
      params,
      isProcessing: true
    }));

    // Process the callback
    processOAuthCallback(params).finally(() => {
      // Mark this callback as processed
      globalCallbackProcessedRef.current = callbackId;
      globalProcessingRef.current = false;
    });
  }, [location, processOAuthCallback]);

  const clearCallback = () => {
    // Clean up URL parameters
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('mercadopago_callback');
    searchParams.delete('code');
    searchParams.delete('state');
    searchParams.delete('error');
    
    const newSearch = searchParams.toString();
    const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
    
    navigate(newUrl, { replace: true });
    
    // Clean up storage
    localStorage.removeItem('mercadopago_oauth_state');
    sessionStorage.removeItem('mercadopago_oauth_state'); // Also clean sessionStorage for backward compatibility
    
    // Reset processing flag (global flag will be reset in finally block)
    processingRef.current = false;
    
    // Reset state
    setCallbackState({
      isProcessing: false,
      isSuccess: false,
      isError: false,
      params: {}
    });
    
    // Reset global state
    globalCallbackProcessedRef.current = null;
  };

  return {
    callbackState,
    clearCallback,
    isCallback: !!callbackState.params.code || !!callbackState.params.error
  };
};
